import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import BotDetailModal from "./BotDetailModal";
import { ArchitectCard } from "./ArchitectCard";
import { CommandsCard } from "./CommandsCard";

// internal scripts need the admin token, so download via the API client instead of a plain link
const downloadScript = async (name) => {
  const r = await api.get(`/downloads/${name}`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  Object.assign(document.createElement("a"), { href: url, download: name }).click();
  URL.revokeObjectURL(url);
};
import { RobustnessCard } from "./RobustnessCard";
import { SymbolsCard } from "./SymbolsCard";
import { OptimizerCard } from "./OptimizerCard";
import {
  RobotIcon,
  DownloadIcon,
  ArrowLeftIcon,
  SignOutIcon,
  ClockClockwiseIcon,
  ChartLineUpIcon,
  BellRingingIcon,
  StarIcon,
  TrophyIcon,
  CalendarIcon,
  RocketLaunchIcon,
  XIcon,
  CopyIcon,
  CheckCircleIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";

const MonthsCell = ({ b, testId }) => {
  const mo = b.backtest_months || 0;
  return (
    <td data-testid={testId} className="px-1 py-2 text-right mono text-[9px]"
      style={{ color: mo >= 24 ? "#64748B" : "#FBBF24" }} title={mo >= 24 ? "24-month backtest" : "Short window - will be re-tested over 24 months"}>
      {mo}mo
    </td>
  );
};


/**
 * MT5 admin lab — v1.9.4 (2026-09-11)
 * Adds profit leaderboard + full metric table (months, sharpe, recovery)
 * so the operator sees exactly what each bot is doing, not just a name.
 */
export default function Mt5AdminOverview() {
  const { logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [fleet, setFleet] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [bts, setBts] = useState({ backtests: [], stats: null });
  const [loading, setLoading] = useState(true);
  const [useBot, setUseBot] = useState(null);  // bot object opened in modal
  const [tickLeaderboard, setTickLeaderboard] = useState([]);
  const [tickBySymbol, setTickBySymbol] = useState([]);
  const [bySymbol, setBySymbol] = useState([]);
  const [tickQueue, setTickQueue] = useState({ counts: {}, recent: [] });
  const [seedingQueue, setSeedingQueue] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [detailBot, setDetailBot] = useState(null);   // bot_name string for detail modal
  const [costStats, setCostStats] = useState(null);
  const [deployAccount, setDeployAccount] = useState("62143440");
  const [deployId, setDeployId] = useState(null);
  const [deployStatus, setDeployStatus] = useState(null);   // pending | in_progress | deployed | failed
  const [deployMsg, setDeployMsg] = useState("");
  const [deploying, setDeploying] = useState(false);

  const startDeploy = async () => {
    if (!useBot || !deployAccount.trim()) return;
    setDeploying(true);
    setDeployStatus("pending");
    setDeployMsg("");
    try {
      const r = await api.post("/mt5admin/deploy", {
        bot_name: useBot.bot_name,
        account_number: deployAccount.trim(),
      });
      setDeployId(r.data.deploy_id);
    } catch (e) {
      setDeployStatus("failed");
      setDeployMsg(e?.response?.data?.detail || "Deploy request failed");
      setDeploying(false);
    }
  };

  useEffect(() => {
    if (!deployId || deployStatus === "deployed" || deployStatus === "failed") return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/mt5admin/deploy/${deployId}`);
        setDeployStatus(r.data.status);
        setDeployMsg(r.data.message || "");
        if (r.data.status === "deployed" || r.data.status === "failed") {
          setDeploying(false);
          clearInterval(t);
        }
      } catch (e) {
        // network glitch — keep polling
      }
    }, 3000);
    return () => clearInterval(t);
  }, [deployId, deployStatus]);

  const closeUseModal = () => {
    setUseBot(null);
    setDeployId(null);
    setDeployStatus(null);
    setDeployMsg("");
    setDeploying(false);
  };

  const load = () => {
    Promise.all([
      api.get("/mt5admin/overview").catch(() => ({ data: null })),
      api.get("/mt5admin/bots?limit=50&sort=champion").catch(() => ({ data: { bots: [] } })),
      api.get("/mt5admin/bots?limit=10&sort=profit").catch(() => ({ data: { bots: [] } })),
      api.get("/mt5admin/backtests?limit=30").catch(() => ({ data: { backtests: [], stats: null } })),
      api.get("/mt5admin/bots/by-symbol?top=5&min_months=0").catch(() => ({ data: { groups: [] } })),
      api.get("/mt5admin/validate/queue").catch(() => ({ data: { counts: {}, recent: [] } })),
      // v1.10 tick-only leaderboard + symbol leaders (test_model=real_ticks)
      api.get("/mt5admin/bots?limit=10&sort=profit&test_model=ticks").catch(() => ({ data: { bots: [] } })),
      api.get("/mt5admin/bots/by-symbol?top=5&min_months=0&test_model=ticks").catch(() => ({ data: { groups: [] } })),
      // v1.11 cost dashboard
      api.get("/mt5admin/cost-stats").catch(() => ({ data: null })),
    ]).then(([o, f, l, x, s, q, tl, ts, cs]) => {
      setStats(o.data || { fleet_size: 0, backtests_today: 0, waitlist: 0, last_heartbeat: null, ready: false });
      setFleet((f.data && f.data.bots) || []);
      setLeaderboard((l.data && l.data.bots) || []);
      setBts(x.data || { backtests: [], stats: null });
      setBySymbol((s.data && s.data.groups) || []);
      setTickQueue(q.data || { counts: {}, recent: [] });
      setTickLeaderboard((tl.data && tl.data.bots) || []);
      setTickBySymbol((ts.data && ts.data.groups) || []);
      setCostStats(cs.data || null);
      setLoading(false);
    });
  };

  const seedTickQueue = async () => {
    setSeedingQueue(true);
    setSeedResult(null);
    try {
      const r = await api.post("/mt5admin/validate/seed", { top: 10, min_backtest_months: 24 });
      setSeedResult(`Added ${r.data.added} bots (skipped ${r.data.skipped} already queued)`);
      load();
    } catch (e) {
      setSeedResult(e?.response?.data?.detail || "Seed failed");
    } finally {
      setSeedingQueue(false);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="mt5-admin-overview" className="relative min-h-screen bg-[#050914] text-[#F1F5F9] overflow-x-hidden">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-10 -left-20 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute top-64 right-0 w-[28rem] h-[28rem] rounded-full bg-yellow-400/10 blur-3xl" />
      </div>

      <header className="relative z-10 border-b border-blue-900/40 backdrop-blur-md bg-[#050914]/80">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/botgenomix-logo-512.png" alt="BotGenomix" className="h-10 w-10 rounded-lg" />
            <div>
              <div className="text-lg font-medium tracking-tight">
                Bot<span className="text-[#FBBF24]">Genomix</span>
                <span className="ml-2 text-[#38BDF8] mono text-xs uppercase tracking-widest">MT5 Lab</span>
              </div>
              <div className="mono text-[9px] uppercase tracking-[0.3em] text-[#64748B]">Expert Advisors pipeline</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin" data-testid="mt5admin-switch-to-sb"
              className="mono text-[10px] uppercase tracking-widest text-[#94A3B8] hover:text-[#F1F5F9] flex items-center gap-2 px-3 py-1.5 rounded-full border border-blue-900/40">
              <ArrowLeftIcon size={12} /> SB Lab
            </Link>
            <Link to="/admin-mt5/portfolio" data-testid="mt5admin-portfolio-link"
              className="mono text-[10px] uppercase tracking-widest text-[#2ECA7F] hover:text-[#6ee7b7] flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-900/40">
              <TrophyIcon size={12} weight="fill" /> Portfolio
            </Link>
            <Link to="/admin-mt5/passed" data-testid="mt5admin-passed-link"
              className="mono text-[10px] uppercase tracking-widest text-[#FBBF24] hover:text-[#fde68a] flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-900/40">
              <TrophyIcon size={12} /> Passed bots
            </Link>
            <Link to="/admin-mt5/customers" data-testid="mt5admin-customers-link"
              className="mono text-[10px] uppercase tracking-widest text-[#38BDF8] flex items-center gap-2 px-3 py-1.5 rounded-full border border-blue-900/40">Customers</Link>
            <Link to="/admin-mt5/emails" data-testid="mt5admin-emails-link"
              className="mono text-[10px] uppercase tracking-widest text-[#E879F9] flex items-center gap-2 px-3 py-1.5 rounded-full border border-fuchsia-900/40">Emails</Link>
            <button onClick={logout} data-testid="mt5admin-logout"
              className="mono text-[10px] uppercase tracking-widest text-[#FF4D4D] hover:text-[#ff8080] flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-900/40">
              <SignOutIcon size={12} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 pt-12 pb-24">
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#FBBF24]">MT5 pipeline · admin</div>
        <h1 className="text-4xl font-medium tracking-tight mt-2">MT5 Expert Advisors fleet.</h1>
        <p className="text-[#94A3B8] mt-3 max-w-3xl">
          Autonomous pipeline. Backend rotates through symbols × strategies and queues Claude-generated MQL5.
          Your VPS compiles, backtests, reports back — 24/7 without you needing to queue anything.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
          <StatTile testid="mt5-stat-fleet" label="Fleet size" value={loading ? "…" : stats?.fleet_size ?? 0} color="#38BDF8" icon={<RobotIcon size={18} weight="duotone" />} />
          <StatTile testid="mt5-stat-backtests" label="Backtests today" value={loading ? "…" : stats?.backtests_today ?? 0} color="#FBBF24" icon={<ClockClockwiseIcon size={18} weight="duotone" />} />
          <StatTile testid="mt5-stat-champions" label="Champions" value={loading ? "…" : stats?.champions ?? 0} color="#D6FF38" icon={<ChartLineUpIcon size={18} weight="duotone" />} />
          <StatTile testid="mt5-stat-waitlist" label="Waitlist emails" value={loading ? "…" : stats?.waitlist ?? 0} color="#E879F9" icon={<BellRingingIcon size={18} weight="duotone" />} />
        </div>

        {/* v1.11 - AI COST DASHBOARD */}
        <CostDashboardTile data-testid="cost-dashboard" costStats={costStats} loading={loading} />

        {/* v1.12 - SLOW BACKTEST MONITOR */}
        <SlowBotsTile data-testid="slow-bots-tile" />

        <div className="mt-6 border border-[#1A1C24] bg-[#0B0D14]/60 rounded-lg p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${stats?.last_heartbeat ? "bg-[#2ECA7F] animate-pulse" : "bg-[#475569]"}`} />
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-[#D6FF38]">Live pipeline</div>
              <div className="text-sm text-[#F1F5F9] mt-1">
                {stats?.last_heartbeat ? `Heartbeat: ${new Date(stats.last_heartbeat).toLocaleString()}` : "Awaiting first heartbeat"}
              </div>
            </div>
          </div>
          {bts.stats && (
            <div className="flex flex-wrap gap-4 mono text-[10px] uppercase tracking-widest">
              <span className="text-[#94A3B8]">last {bts.stats.total} tests</span>
              <span className="text-[#22D3EE]">with trades: {bts.stats.with_trades}</span>
              <span className="text-[#FBBF24]">avg trades: {bts.stats.avg_trades}</span>
              <span className="text-[#D6FF38]">best PF: {(bts.stats.best_pf || 0).toFixed(2)}</span>
            </div>
          )}
        </div>

        {/* TICK VALIDATION QUEUE PANEL */}
        <div data-testid="tick-queue-panel" className="mt-6 border border-[#E879F9]/40 bg-gradient-to-br from-[#E879F9]/[0.06] to-transparent rounded-lg overflow-hidden">
          <div className="px-5 py-4 border-b border-[#E879F9]/30 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <WarningCircleIcon size={22} weight="fill" style={{ color: "#E879F9" }} />
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-[#E879F9]">Real-tick validation</div>
                <div className="text-lg font-medium">Bar → Tick truth check</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="mono text-[10px] uppercase tracking-widest text-[#94A3B8]">
                pending <span className="text-[#FBBF24]">{tickQueue.counts?.pending ?? 0}</span> · running <span className="text-[#22D3EE]">{tickQueue.counts?.picked_up ?? 0}</span> · passed <span className="text-[#2ECA7F]">{tickQueue.counts?.passed ?? 0}</span> · failed <span className="text-[#F87171]">{tickQueue.counts?.failed ?? 0}</span>
              </span>
              <button
                data-testid="tick-seed-btn"
                onClick={seedTickQueue}
                disabled={seedingQueue}
                className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition disabled:opacity-40"
                style={{ borderColor: "#E879F9", color: "#E879F9", background: "#E879F910" }}
              >
                {seedingQueue ? "seeding…" : "Seed top-10/symbol"}
              </button>
            </div>
          </div>
          {seedResult && (<div className="px-5 py-2 mono text-[10px] text-[#E879F9]">{seedResult}</div>)}
          {(tickQueue.recent || []).length === 0 ? (
            <div className="p-6 text-center mono text-xs text-[#64748B]">
              Queue empty. Click <span className="text-[#E879F9]">Seed top-10/symbol</span> to send your best bar-model bots to the real-tick validator running on the VPS.
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[280px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#050B18]">
                  <tr className="mono text-[10px] uppercase tracking-widest text-[#64748B]">
                    <th className="text-left px-3 py-2">Bot</th>
                    <th className="text-left px-3 py-2">Sym</th>
                    <th className="text-right px-3 py-2">Bars PnL</th>
                    <th className="text-right px-3 py-2">Tick PnL</th>
                    <th className="text-right px-3 py-2">Bars PF</th>
                    <th className="text-right px-3 py-2">Tick PF</th>
                    <th className="text-right px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tickQueue.recent.map((r) => {
                    const bp = r.bars_stats?.net_profit ?? 0;
                    const tp = r.tick_stats?.net_profit;
                    const bpf = r.bars_stats?.profit_factor ?? 0;
                    const tpf = r.tick_stats?.profit_factor;
                    const cLabel = r.status === "passed" ? "#2ECA7F" : r.status === "failed" ? "#F87171" : "#FBBF24";
                    return (
                      <tr key={r.task_id || r.bot_name} className="border-t border-[#E879F9]/10 hover:bg-[#E879F909]" data-testid={`tick-row-${r.bot_name}`}>
                        <td className="px-3 py-2 mono text-[10.5px] truncate max-w-[220px]">
                          <button
                            onClick={() => setDetailBot(r.bot_name)}
                            className="text-left hover:text-[#E879F9] hover:underline transition truncate max-w-full"
                          >{r.bot_name}</button>
                        </td>
                        <td className="px-3 py-2 mono text-[10px] text-[#94A3B8]">{r.symbol}</td>
                        <td className="px-3 py-2 text-right mono text-[10px]" style={{ color: bp > 0 ? "#2ECA7F" : "#F87171" }}>£{Number(bp).toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                        <td className="px-3 py-2 text-right mono text-[10px]" style={{ color: tp == null ? "#64748B" : tp > 0 ? "#2ECA7F" : "#F87171" }}>{tp == null ? "—" : `£${Number(tp).toLocaleString(undefined,{maximumFractionDigits:0})}`}</td>
                        <td className="px-3 py-2 text-right mono text-[10px] text-[#94A3B8]">{Number(bpf).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right mono text-[10px]" style={{ color: tpf == null ? "#64748B" : tpf >= 1.2 ? "#D6FF38" : "#F87171" }}>{tpf == null ? "—" : Number(tpf).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right mono text-[9px] uppercase tracking-widest" style={{ color: cLabel }}>{r.status}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* TICK CHAMPIONS LEADERBOARD (real-tick backtested only) */}
        <div data-testid="tick-champions-card" className="mt-10 border border-[#2ECA7F]/40 bg-gradient-to-br from-[#2ECA7F]/[0.05] to-transparent rounded-lg overflow-hidden">
          <div className="px-5 py-4 border-b border-[#2ECA7F]/30 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TrophyIcon size={22} weight="fill" style={{ color: "#2ECA7F" }} />
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-[#2ECA7F]">Real-tick champions</div>
                <div className="text-lg font-medium">Top 10 · Model=4 backtests only</div>
              </div>
            </div>
            <span className="mono text-[10px] text-[#64748B]">{tickLeaderboard.length} bots</span>
          </div>
          {loading ? (
            <div className="p-8 text-center mono text-xs text-[#64748B]">loading …</div>
          ) : tickLeaderboard.length === 0 ? (
            <div className="p-8 text-center mono text-xs text-[#64748B]">
              No tick-tested bots yet. Once <span className="text-[#2ECA7F]">Run_MT5_Brain.ps1 v1.9.11</span> runs on the VPS, real-tick champions will appear here automatically.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="mono text-[10px] uppercase tracking-widest text-[#64748B] bg-[#050B18]">
                    <th className="text-left px-4 py-2.5 w-10">#</th>
                    <th className="text-left px-4 py-2.5">Bot</th>
                    <th className="text-left px-4 py-2.5">Sym</th>
                    <th className="text-right px-4 py-2.5">Mo</th>
                    <th className="text-right px-4 py-2.5">Trades</th>
                    <th className="text-right px-4 py-2.5">Mo+/-</th>
                    <th className="text-right px-4 py-2.5">PF</th>
                    <th className="text-right px-4 py-2.5">Sharpe</th>
                    <th className="text-right px-4 py-2.5">DD %</th>
                    <th className="text-right px-4 py-2.5">Net PnL</th>
                    <th className="text-right px-4 py-2.5">Use</th>
                  </tr>
                </thead>
                <tbody>
                  {tickLeaderboard.map((b, i) => (
                    <tr key={b.bot_name} className={`border-t border-[#2ECA7F]/10 hover:bg-[#2ECA7F]/5 ${i < 3 ? "bg-[#2ECA7F]/[0.03]" : ""}`} data-testid={`tick-leaderboard-row-${i}`}>
                      <td className="px-4 py-2.5 mono text-[13px] font-medium" style={{ color: i === 0 ? "#FBBF24" : i < 3 ? "#22D3EE" : "#64748B" }}>
                        {i < 3 && <TrophyIcon size={12} weight="fill" className="inline mr-1" style={{ color: i === 0 ? "#FBBF24" : "#22D3EE" }} />}
                        {i + 1}
                      </td>
                      <td className="px-4 py-2.5 mono text-[11px] truncate max-w-[220px]">
                        {b.is_champion && (<StarIcon size={12} weight="fill" className="inline-block mr-1 text-[#2ECA7F]" />)}
                        <button data-testid={`tick-leaderboard-name-${i}`} onClick={() => setDetailBot(b.bot_name)}
                          className="text-left hover:text-[#2ECA7F] hover:underline transition">{b.bot_name}</button>
                        <span className="ml-1 mono text-[8px] uppercase px-1 py-0.5 rounded" style={{ background: "#2ECA7F20", color: "#2ECA7F" }}>TICK</span>
                      </td>
                      <td className="px-4 py-2.5 mono text-[11px] text-[#94A3B8]">{b.symbol}</td>
                      <td className="px-4 py-2.5 text-right mono text-[11px] text-[#94A3B8]">{b.backtest_months || 0}</td>
                      <td className="px-4 py-2.5 text-right mono">{b.total_trades ?? 0}</td>
                      <td className="px-4 py-2.5 text-right mono text-[11px]">
                        <span className="text-[#2ECA7F]">{b.profit_months ?? 0}</span><span className="text-[#64748B]">/</span><span className="text-[#F87171]">{b.losing_months ?? 0}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right mono" style={{ color: (b.profit_factor ?? 0) >= 1.5 ? "#D6FF38" : (b.profit_factor ?? 0) >= 1.2 ? "#22D3EE" : "#94A3B8" }}>{(b.profit_factor ?? 0).toFixed(2)}</td>
                      <td className="px-4 py-2.5 text-right mono text-[11px]" style={{ color: (b.sharpe_ratio ?? 0) >= 1 ? "#D6FF38" : "#94A3B8" }}>{(b.sharpe_ratio ?? 0).toFixed(2)}</td>
                      <td className="px-4 py-2.5 text-right mono text-[#94A3B8]">{(b.max_dd_pct ?? 0).toFixed(1)}</td>
                      <td className="px-4 py-2.5 text-right mono font-medium" style={{ color: (b.net_profit ?? 0) > 0 ? "#2ECA7F" : "#F87171" }}>£{(b.net_profit ?? 0).toFixed(2)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <button data-testid={`tick-use-${i}`} onClick={() => setUseBot(b)}
                          className="mono text-[9px] uppercase tracking-widest px-2.5 py-1 rounded border transition"
                          style={{ borderColor: "#2ECA7F", color: "#2ECA7F", background: "#2ECA7F10" }}>
                          <RocketLaunchIcon size={11} weight="fill" className="inline mr-1" /> Use
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* TICK SYMBOL LEADERS (real-tick only, top 5 per symbol) */}
        <div className="mt-6 border border-[#2ECA7F]/30 bg-[#020617]/60 rounded-lg overflow-hidden" data-testid="tick-by-symbol-card">
          <div className="px-5 py-4 border-b border-[#2ECA7F]/20 flex items-center justify-between">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-[#2ECA7F]">Real-tick · top 5 per symbol · all backtests (amber = under 24mo)</div>
              <div className="text-lg font-medium">Tick symbol leaders</div>
            </div>
            <span className="mono text-[10px] text-[#64748B]">{tickBySymbol.length} symbols</span>
          </div>
          {loading ? (
            <div className="p-8 text-center mono text-xs text-[#64748B]">loading …</div>
          ) : tickBySymbol.length === 0 ? (
            <div className="p-8 text-center mono text-xs text-[#64748B]">No tick symbol leaders yet — the tick-only brain is still warming up.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
              {tickBySymbol.map((grp) => (
                <div key={grp.symbol} className="border border-[#2ECA7F]/20 bg-[#050914]/70 rounded-md overflow-hidden" data-testid={`tick-symbol-group-${grp.symbol}`}>
                  <div className="px-4 py-2.5 border-b border-[#2ECA7F]/20 flex items-center justify-between bg-[#0B0D14]">
                    <div className="mono text-sm font-medium text-[#F1F5F9]">{grp.symbol}</div>
                    <div className="mono text-[9px] uppercase tracking-widest text-[#2ECA7F]">{grp.count} tick bots</div>
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      {grp.bots.map((b, i) => (
                        <tr key={b.bot_name} className="border-t border-[#2ECA7F]/10 hover:bg-[#2ECA7F08]">
                          <td className="px-3 py-2 mono text-[10px] w-6 text-[#64748B]">{i + 1}</td>
                          <td className="px-2 py-2 mono text-[10px] truncate max-w-[130px]">
                            <button onClick={() => setDetailBot(b.bot_name)} className="text-left hover:text-[#2ECA7F] hover:underline transition truncate max-w-full">{b.bot_name}</button>
                          </td>
                          <MonthsCell b={b} testId={`tick-symbol-${grp.symbol}-months-${i}`} />
                          <td className="px-2 py-2 text-right mono text-[10px]" style={{ color: (b.profit_factor ?? 0) >= 1.2 ? "#2ECA7F" : "#94A3B8" }}>{(b.profit_factor ?? 0).toFixed(2)}</td>
                          <td className="px-2 py-2 text-right mono text-[10px]" style={{ color: (b.net_profit ?? 0) > 0 ? "#2ECA7F" : "#F87171" }}>£{(b.net_profit ?? 0).toFixed(0)}</td>
                          <td className="px-2 py-2 text-right">
                            <button onClick={() => setUseBot(b)} className="mono text-[8px] uppercase tracking-widest px-2 py-0.5 rounded border transition" style={{ borderColor: "#2ECA7F", color: "#2ECA7F", background: "#2ECA7F10" }}>Use</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* PROFIT LEADERBOARD */}
        <div className="mt-8 border border-[#D6FF38]/40 bg-gradient-to-br from-[#D6FF38]/[0.04] to-transparent rounded-lg overflow-hidden">
          <div className="px-5 py-4 border-b border-[#D6FF38]/30 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TrophyIcon size={22} weight="fill" style={{ color: "#D6FF38" }} />
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-[#D6FF38]">Profit leaderboard</div>
                <div className="text-lg font-medium">Top 10 by net PnL</div>
              </div>
            </div>
            <span className="mono text-[10px] text-[#64748B]">auto-refresh 15s</span>
          </div>
          {loading ? (
            <div className="p-8 text-center mono text-xs text-[#64748B]">loading …</div>
          ) : leaderboard.length === 0 ? (
            <div className="p-8 text-center mono text-xs text-[#64748B]">No bots ingested yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="mono text-[10px] uppercase tracking-widest text-[#64748B] bg-[#050B18]">
                    <th className="text-left px-4 py-2.5 w-10">#</th>
                    <th className="text-left px-4 py-2.5">Bot</th>
                    <th className="text-left px-4 py-2.5">Sym</th>
                    <th className="text-right px-4 py-2.5">Period</th>
                    <th className="text-right px-4 py-2.5">Trades</th>
                    <th className="text-right px-4 py-2.5">Prof/Loss mo</th>
                    <th className="text-right px-4 py-2.5">PF</th>
                    <th className="text-right px-4 py-2.5">Sharpe</th>
                    <th className="text-right px-4 py-2.5">DD %</th>
                    <th className="text-right px-4 py-2.5">Net PnL</th>
                    <th className="text-right px-4 py-2.5">Use</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((b, i) => (
                    <tr key={b.bot_name} className={`border-t border-[#D6FF38]/10 hover:bg-[#D6FF38]/5 ${i < 3 ? "bg-[#FBBF24]/[0.03]" : ""}`} data-testid={`leaderboard-row-${i}`}>
                      <td className="px-4 py-2.5 mono text-[13px] font-medium" style={{ color: i === 0 ? "#FBBF24" : i < 3 ? "#D6FF38" : "#64748B" }}>
                        {i < 3 && <TrophyIcon size={12} weight="fill" className="inline mr-1" style={{ color: i === 0 ? "#FBBF24" : "#D6FF38" }} />}
                        {i + 1}
                      </td>
                      <td className="px-4 py-2.5 mono text-[11px] truncate max-w-[220px]">
                        {b.is_champion && (<StarIcon size={12} weight="fill" className="inline-block mr-1 text-[#D6FF38]" />)}
                        <button
                          data-testid={`leaderboard-name-${i}`}
                          onClick={() => setDetailBot(b.bot_name)}
                          className="text-left hover:text-[#D6FF38] hover:underline transition"
                        >
                          {b.bot_name}
                        </button>
                        {b.tick_validated === true && (<span data-testid={`tick-chip-${i}`} className="ml-1 mono text-[8px] uppercase px-1 py-0.5 rounded" style={{ background: "#2ECA7F20", color: "#2ECA7F" }}>TICK</span>)}
                        {b.tick_status === "failed" && (<span className="ml-1 mono text-[8px] uppercase px-1 py-0.5 rounded" style={{ background: "#F8717120", color: "#F87171" }}>FAIL-T</span>)}
                        {b.tick_tested !== true && (<span className="ml-1 mono text-[8px] uppercase px-1 py-0.5 rounded" style={{ background: "#33415530", color: "#64748B" }}>BAR</span>)}
                      </td>
                      <td className="px-4 py-2.5 mono text-[11px] text-[#94A3B8]">{b.symbol}</td>
                      <td className="px-4 py-2.5 text-right mono text-[11px] text-[#94A3B8]">{b.backtest_months || 0} mo</td>
                      <td className="px-4 py-2.5 text-right mono">{b.total_trades ?? 0}</td>
                      <td className="px-4 py-2.5 text-right mono text-[11px]">
                        <span className="text-[#2ECA7F]">{b.profit_months ?? 0}</span>
                        <span className="text-[#64748B]">/</span>
                        <span className="text-[#F87171]">{b.losing_months ?? 0}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right mono" style={{ color: (b.profit_factor ?? 0) >= 1.2 ? "#D6FF38" : (b.profit_factor ?? 0) >= 1.0 ? "#22D3EE" : "#94A3B8" }}>
                        {(b.profit_factor ?? 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-2.5 text-right mono text-[11px]" style={{ color: (b.sharpe_ratio ?? 0) >= 1 ? "#D6FF38" : "#94A3B8" }}>
                        {(b.sharpe_ratio ?? 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-2.5 text-right mono text-[#94A3B8]">{(b.max_dd_pct ?? 0).toFixed(1)}</td>
                      <td className="px-4 py-2.5 text-right mono font-medium" style={{ color: (b.net_profit ?? 0) > 0 ? "#2ECA7F" : "#F87171" }}>
                        £{(b.net_profit ?? 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          data-testid={`use-bot-${i}`}
                          onClick={() => setUseBot(b)}
                          className="mono text-[9px] uppercase tracking-widest px-2.5 py-1 rounded border transition"
                          style={{ borderColor: "#D6FF38", color: "#D6FF38", background: "#D6FF3810" }}
                        >
                          <RocketLaunchIcon size={11} weight="fill" className="inline mr-1" />
                          Use
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <CommandsCard />
        <SymbolsCard />
        <OptimizerCard />
        <RobustnessCard />
        <ArchitectCard />

        <div className="mt-10 border border-[#D6FF38]/30 bg-[#020617]/60 rounded-lg overflow-hidden" data-testid="mt5-by-symbol-card">
          <div className="px-5 py-4 border-b border-[#D6FF38]/20 flex items-center justify-between">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-[#D6FF38]">Top 5 per symbol · all backtests (amber = under 24mo)</div>
              <div className="text-lg font-medium">Symbol leaders</div>
            </div>
            <span className="mono text-[10px] text-[#64748B]">{bySymbol.length} symbols</span>
          </div>
          {loading ? (
            <div className="p-8 text-center mono text-xs text-[#64748B]">loading …</div>
          ) : bySymbol.length === 0 ? (
            <div className="p-8 text-center mono text-xs text-[#64748B]">No bots yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
              {bySymbol.map((grp) => (
                <div key={grp.symbol} className="border border-[#1F2937] bg-[#050914]/70 rounded-md overflow-hidden" data-testid={`symbol-group-${grp.symbol}`}>
                  <div className="px-4 py-2.5 border-b border-[#1F2937] flex items-center justify-between bg-[#0B0D14]">
                    <div className="mono text-sm font-medium text-[#F1F5F9]">{grp.symbol}</div>
                    <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">{grp.count} bots</div>
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      {grp.bots.map((b, i) => (
                        <tr key={b.bot_name} className="border-t border-[#1F2937] hover:bg-[#D6FF3808]" data-testid={`symbol-${grp.symbol}-rank-${i}`}>
                          <td className="px-3 py-2 mono text-[10px] w-6 text-[#64748B]">{i + 1}</td>
                          <td className="px-2 py-2 mono text-[10px] truncate max-w-[130px]">
                            <button
                              data-testid={`symbol-name-${grp.symbol}-${i}`}
                              onClick={() => setDetailBot(b.bot_name)}
                              className="text-left hover:text-[#D6FF38] hover:underline transition truncate max-w-full"
                            >
                              {b.bot_name}
                            </button>
                          </td>
                          <MonthsCell b={b} testId={`symbol-${grp.symbol}-months-${i}`} />
                          <td className="px-2 py-2 text-right mono text-[10px]" style={{ color: (b.profit_factor ?? 0) >= 1.2 ? "#D6FF38" : "#94A3B8" }}>
                            {(b.profit_factor ?? 0).toFixed(2)}
                          </td>
                          <td className="px-2 py-2 text-right mono text-[10px]" style={{ color: (b.net_profit ?? 0) > 0 ? "#2ECA7F" : "#F87171" }}>
                            £{(b.net_profit ?? 0).toFixed(0)}
                          </td>
                          <td className="px-2 py-2 text-right">
                            <button
                              data-testid={`use-symbol-${grp.symbol}-${i}`}
                              onClick={() => setUseBot(b)}
                              className="mono text-[8px] uppercase tracking-widest px-2 py-0.5 rounded border transition"
                              style={{ borderColor: "#D6FF38", color: "#D6FF38", background: "#D6FF3810" }}
                            >
                              Use
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-blue-900/40 bg-[#020617]/60 rounded-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-blue-900/40 flex items-center justify-between">
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-[#38BDF8]">Full fleet</div>
                <div className="text-lg font-medium">All ingested bots</div>
              </div>
              <span className="mono text-[10px] text-[#64748B]">{fleet.length} bots</span>
            </div>
            {loading ? (
              <div className="p-8 text-center mono text-xs text-[#64748B]">loading …</div>
            ) : fleet.length === 0 ? (
              <div className="p-8 text-center mono text-xs text-[#64748B]">No bots ingested yet.</div>
            ) : (
              <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[#050B18]">
                    <tr className="mono text-[10px] uppercase tracking-widest text-[#64748B]">
                      <th className="text-left px-3 py-2.5">Bot</th>
                      <th className="text-right px-3 py-2.5">Tr</th>
                      <th className="text-right px-3 py-2.5">PF</th>
                      <th className="text-right px-3 py-2.5">DD</th>
                      <th className="text-right px-3 py-2.5">Mo+/-</th>
                      <th className="text-right px-3 py-2.5">Rec</th>
                      <th className="text-right px-3 py-2.5">PnL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fleet.map((b) => (
                      <tr key={b.bot_name} className="border-t border-blue-900/20 hover:bg-blue-900/10" data-testid={`mt5-bot-row-${b.bot_name}`}>
                        <td className="px-3 py-2.5 mono text-[10.5px] truncate max-w-[180px]">
                          {b.is_champion && (<StarIcon size={11} weight="fill" className="inline-block mr-1 text-[#D6FF38]" />)}
                          <button
                            data-testid={`fleet-name-${b.bot_name}`}
                            onClick={() => setDetailBot(b.bot_name)}
                            className="text-left hover:text-[#D6FF38] hover:underline transition truncate max-w-full"
                          >
                            {b.bot_name}
                          </button>
                        </td>
                        <td className="px-3 py-2.5 text-right mono text-[11px]">{b.total_trades ?? 0}</td>
                        <td className="px-3 py-2.5 text-right mono text-[11px]" style={{ color: (b.profit_factor ?? 0) >= 1.2 ? "#D6FF38" : (b.profit_factor ?? 0) >= 1.0 ? "#22D3EE" : "#94A3B8" }}>
                          {(b.profit_factor ?? 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2.5 text-right mono text-[11px] text-[#94A3B8]">{(b.max_dd_pct ?? 0).toFixed(1)}</td>
                        <td className="px-3 py-2.5 text-right mono text-[10px]">
                          <span className="text-[#2ECA7F]">{b.profit_months ?? 0}</span>
                          <span className="text-[#64748B]">/</span>
                          <span className="text-[#F87171]">{b.losing_months ?? 0}</span>
                        </td>
                        <td className="px-3 py-2.5 text-right mono text-[10px] text-[#94A3B8]">{(b.recovery_factor ?? 0).toFixed(1)}</td>
                        <td className="px-3 py-2.5 text-right mono text-[11px]" style={{ color: (b.net_profit ?? 0) > 0 ? "#2ECA7F" : "#F87171" }}>
                          £{(b.net_profit ?? 0).toFixed(0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="border border-yellow-900/40 bg-[#0B0B04]/60 rounded-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-yellow-900/40 flex items-center justify-between">
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-[#FBBF24]">Recent activity</div>
                <div className="text-lg font-medium">Backtests · last 30</div>
              </div>
              <span className="mono text-[10px] text-[#64748B]">auto-refresh 15s</span>
            </div>
            {loading ? (
              <div className="p-8 text-center mono text-xs text-[#64748B]">loading …</div>
            ) : bts.backtests.length === 0 ? (
              <div className="p-8 text-center mono text-xs text-[#64748B]">No backtests yet.</div>
            ) : (
              <div className="overflow-y-auto max-h-[520px] divide-y divide-yellow-900/20">
                {bts.backtests.map((b, i) => (
                  <div key={b.task_id || i} className="px-5 py-3 flex items-center gap-3 hover:bg-yellow-900/5" data-testid={`mt5-recent-${i}`}>
                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      b.total_trades > 0 && b.profit_factor >= 1.2 ? "bg-[#D6FF38]"
                        : b.total_trades > 0 ? "bg-[#22D3EE]"
                        : b.compiled_ok ? "bg-[#94A3B8]"
                        : "bg-[#F87171]"
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="mono text-[11px] truncate">{b.bot_name}</div>
                      <div className="mono text-[9px] text-[#64748B] uppercase tracking-widest flex items-center gap-2">
                        <span>{b.symbol}</span>
                        {b.backtest_months > 0 && (
                          <span className="flex items-center gap-1"><CalendarIcon size={9} /> {b.backtest_months}mo</span>
                        )}
                        <span>{b.ts ? new Date(b.ts).toLocaleTimeString() : "-"}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="mono text-[11px]" style={{ color: (b.net_profit ?? 0) > 0 ? "#2ECA7F" : (b.net_profit ?? 0) < 0 ? "#F87171" : "#64748B" }}>
                        £{(b.net_profit ?? 0).toFixed(2)}
                      </div>
                      <div className="mono text-[9px] text-[#94A3B8]">
                        {b.total_trades ?? 0} tr · PF {(b.profit_factor ?? 0).toFixed(2)}
                        {b.profit_months !== undefined && (
                          <span> · <span className="text-[#2ECA7F]">{b.profit_months}</span>/<span className="text-[#F87171]">{b.losing_months}</span></span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 border border-yellow-900/40 bg-[#0B0B04]/60 rounded-lg p-6 flex flex-col md:flex-row gap-6 justify-between">
          <div className="max-w-2xl">
            <div className="mono text-[10px] uppercase tracking-widest text-[#FBBF24]">Starter kit</div>
            <h3 className="text-xl font-medium mt-2">MT5 v1.9.9 — locale-aware parser + bridge agent</h3>
            <p className="text-xs text-[#94A3B8] mt-2 leading-relaxed">
              Parser now handles the NBSP thousands-separator MT5 build 6182 emits so bots with
              5-figure profit stop being truncated to 2 digits. Bridge agent enables the USE button
              to drop the .ex5 straight into your demo terminal's Experts folder — no manual copy.
            </p>
          </div>
          <div className="flex flex-col gap-2 self-start">
            {[["mt5_starter_v64.zip", "Starter v64 (secured)", "mt5-download-starter"], ["mt5_bridge_v1.zip", "Bridge v1.0", "mt5-download-bridge"]].map(([f, l, id]) => (
              <button key={f} onClick={() => downloadScript(f)} data-testid={id}
                className="inline-flex items-center gap-2 mono text-[11px] uppercase tracking-widest px-4 py-2.5 rounded-md border border-yellow-500 text-[#FBBF24] hover:bg-yellow-500/10 transition whitespace-nowrap">
                <DownloadIcon size={14} /> {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {useBot && (
        <div data-testid="use-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
             onClick={closeUseModal}>          <div className="relative w-full max-w-md rounded-lg border border-[#D6FF38]/40 bg-[#050914] shadow-2xl"
               onClick={(e) => e.stopPropagation()}>
            <button data-testid="use-modal-close" onClick={closeUseModal}
              className="absolute top-3 right-3 text-[#64748B] hover:text-[#F1F5F9]">
              <XIcon size={18} />
            </button>
            <div className="p-6 border-b border-[#D6FF38]/20">
              <div className="mono text-[10px] uppercase tracking-widest text-[#D6FF38] mb-2 flex items-center gap-2">
                <RocketLaunchIcon size={12} weight="fill" /> Deploy to demo MT5
              </div>
              <div className="mono text-sm text-[#F1F5F9] truncate">{useBot.bot_name}</div>
              <div className="mt-3 grid grid-cols-3 gap-3 mono text-[10px]">
                <div><div className="text-[#64748B] uppercase tracking-widest">Symbol</div><div className="text-[#F1F5F9]">{useBot.symbol}</div></div>
                <div><div className="text-[#64748B] uppercase tracking-widest">Months</div><div className="text-[#F1F5F9]">{useBot.backtest_months || "-"}</div></div>
                <div><div className="text-[#64748B] uppercase tracking-widest">PnL</div><div className="text-[#2ECA7F]">£{(useBot.net_profit ?? 0).toFixed(0)}</div></div>
                <div><div className="text-[#64748B] uppercase tracking-widest">PF</div><div className="text-[#F1F5F9]">{(useBot.profit_factor ?? 0).toFixed(2)}</div></div>
                <div><div className="text-[#64748B] uppercase tracking-widest">DD</div><div className="text-[#F1F5F9]">{(useBot.max_dd_pct ?? 0).toFixed(1)}%</div></div>
                <div><div className="text-[#64748B] uppercase tracking-widest">Sharpe</div><div className="text-[#F1F5F9]">{(useBot.sharpe_ratio ?? 0).toFixed(2)}</div></div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="mono text-[10px] uppercase tracking-widest text-[#64748B]">Demo account number</label>
                <input
                  data-testid="deploy-account-input"
                  type="text"
                  value={deployAccount}
                  onChange={(e) => setDeployAccount(e.target.value)}
                  disabled={deploying || deployStatus === "deployed"}
                  className="mt-1 w-full mono text-sm bg-[#0B0D14] border border-[#1F2937] rounded px-3 py-2 text-[#F1F5F9] focus:outline-none focus:border-[#D6FF38]"
                  placeholder="e.g. 62143440"
                />
              </div>

              {deployStatus === "pending" && (
                <div data-testid="deploy-status-pending" className="mono text-[11px] text-[#22D3EE]">
                  <ClockClockwiseIcon size={12} className="inline mr-1" />
                  Waiting for VPS bridge to pick up... (polls every 15s)
                </div>
              )}
              {deployStatus === "in_progress" && (
                <div data-testid="deploy-status-progress" className="mono text-[11px] text-[#FBBF24]">
                  Bridge is copying the .ex5...
                </div>
              )}
              {deployStatus === "deployed" && (
                <div data-testid="deploy-status-ok" className="mono text-[11px] text-[#2ECA7F] leading-relaxed">
                  ✅ Deployed!  {deployMsg}
                  <div className="mt-2 text-[#94A3B8]">Now in MT5: right-click Navigator → Refresh → open {useBot.symbol} M15 → drag EA on → enable AutoTrading.</div>
                </div>
              )}
              {deployStatus === "failed" && (
                <div data-testid="deploy-status-fail" className="mono text-[11px] text-[#F87171] leading-relaxed">
                  ❌ Failed: {deployMsg}
                </div>
              )}

              <button
                data-testid="deploy-confirm-btn"
                onClick={startDeploy}
                disabled={deploying || deployStatus === "deployed" || !deployAccount.trim()}
                className="w-full mono text-[11px] uppercase tracking-widest px-4 py-3 rounded-md transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:scale-[1.02]"
                style={{ background: "#D6FF38", color: "#050914" }}
              >
                {deployStatus === "deployed"
                  ? "Deployed ✓"
                  : deploying
                    ? "Deploying..."
                    : <><RocketLaunchIcon size={12} weight="fill" className="inline mr-1" /> Deploy to account {deployAccount || "…"}</>}
              </button>

              <div className="mono text-[9px] text-[#64748B] leading-relaxed">
                Requires MT5_Bridge.ps1 running on the VPS. Account must have logged into MT5 at least once so the bridge can find its terminal folder.
              </div>
            </div>
          </div>
        </div>
      )}

      {detailBot && (
        <BotDetailModal
          botName={detailBot}
          onClose={() => setDetailBot(null)}
          onUse={(bot) => setUseBot(bot)}
        />
      )}
    </div>
  );
}

function StatTile({ testid, label, value, color, icon }) {
  return (
    <div data-testid={testid} className="relative border border-[#1A1C24] bg-[#0B0D14]/60 rounded-lg p-4 overflow-hidden">
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full blur-3xl opacity-40" style={{ background: color }} />
      <div className="relative">
        <div className="flex items-center justify-between">
          <div className="mono text-[10px] uppercase tracking-widest text-[#64748B]">{label}</div>
          <div style={{ color }}>{icon}</div>
        </div>
        <div className="mono text-3xl font-medium mt-2" style={{ color }}>{value}</div>
      </div>
    </div>
  );
}

// v1.11 - AI cost monitor. NO cap, just visibility.
function CostDashboardTile({ costStats, loading }) {
  if (loading || !costStats) {
    return (
      <div data-testid="cost-dashboard" className="mt-6 border border-[#22D3EE]/30 rounded-lg p-5 bg-[#0B0D14]/60">
        <div className="mono text-[10px] uppercase tracking-widest text-[#22D3EE]">AI credit spend</div>
        <div className="mono text-xs text-[#64748B] mt-2">Loading…</div>
      </div>
    );
  }
  const d24 = costStats.last_24h || {};
  const d7  = costStats.last_7d  || {};
  const d30 = costStats.last_30d || {};
  const daily = costStats.daily_7d || [];
  const maxDaily = Math.max(1, ...daily.map((x) => x.calls || 0));

  return (
    <div data-testid="cost-dashboard" className="mt-6 border border-[#22D3EE]/30 rounded-lg p-5 bg-gradient-to-br from-[#22D3EE]/[0.05] to-transparent">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-[#22D3EE]">AI credit spend · monitor</div>
          <div className="text-lg font-medium">Claude Haiku · {costStats.model}</div>
        </div>
        <span className="mono text-[9px] uppercase tracking-widest text-[#94A3B8]">
          {costStats.cap_enabled ? "cap ON" : "cap OFF · monitoring only"}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <CostCell testid="cost-24h" label="Last 24h" calls={d24.haiku_calls} muts={d24.mutation_calls} gbp={d24.estimated_cost_gbp} color="#22D3EE" />
        <CostCell testid="cost-7d"  label="Last 7 days"  calls={d7.haiku_calls}  muts={d7.mutation_calls}  gbp={d7.estimated_cost_gbp}  color="#38BDF8" />
        <CostCell testid="cost-30d" label="Last 30 days" calls={d30.haiku_calls} muts={d30.mutation_calls} gbp={d30.estimated_cost_gbp} color="#818CF8" />
      </div>

      {/* Sparkline */}
      {daily.length > 0 && (
        <div className="mt-4">
          <div className="mono text-[10px] uppercase tracking-widest text-[#64748B] mb-2">7-day daily calls</div>
          <div className="flex items-end gap-1 h-16">
            {daily.map((d, i) => {
              const h = Math.max(2, Math.round(((d.calls || 0) / maxDaily) * 60));
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.calls} calls · £${d.gbp}`}>
                  <div className="w-full rounded-t" style={{ height: `${h}px`, background: "#22D3EE", opacity: 0.7 }} />
                  <div className="mono text-[8px] text-[#64748B] truncate">{d.date.slice(5)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function CostCell({ testid, label, calls, muts, gbp, color }) {
  return (
    <div data-testid={testid} className="border border-[#1F2937] rounded-md p-3 bg-[#050914]/60">
      <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">{label}</div>
      <div className="mono text-2xl font-medium mt-1" style={{ color }}>£{Number(gbp || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
      <div className="mono text-[10px] text-[#94A3B8] mt-1">
        {calls || 0} Haiku · <span className="text-[#2ECA7F]">{muts || 0} free mutations</span>
      </div>
    </div>
  );
}

// v1.12 - top slowest backtests + slowest (symbol,tf) cells over 24h
function SlowBotsTile() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    api.get("/mt5admin/slow-bots?limit=8&hours=24")
      .then((r) => setData(r.data))
      .catch(() => setData({ slowest_runs: [], by_cell: [] }));
  }, []);
  const runs = data?.slowest_runs || [];
  const cells = data?.by_cell || [];
  const fmtSecs = (s) => {
    s = Number(s || 0);
    if (s < 60) return `${s.toFixed(1)}s`;
    return `${Math.floor(s/60)}m ${(s%60).toFixed(0)}s`;
  };
  return (
    <div data-testid="slow-bots-tile" className="mt-6 border border-[#F59E0B]/30 rounded-lg p-5 bg-gradient-to-br from-[#F59E0B]/[0.04] to-transparent">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-[#F59E0B]">Slowest backtests · last 24h</div>
          <div className="text-lg font-medium">Wall-time watchlist</div>
        </div>
        <span className="mono text-[9px] uppercase tracking-widest text-[#94A3B8]">cap 12min · killed = timeout</span>
      </div>
      {!data ? (
        <div className="mono text-xs text-[#64748B]">Loading…</div>
      ) : runs.length === 0 ? (
        <div className="mono text-xs text-[#64748B]">No backtests yet — check back after a few cycles.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {/* Individual slowest runs */}
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-[#64748B] mb-2">Slowest individual runs</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="mono text-[9px] uppercase tracking-widest text-[#64748B]">
                  <th className="text-left py-1">Bot</th>
                  <th className="text-left py-1">Sym·TF</th>
                  <th className="text-right py-1">Time</th>
                  <th className="text-right py-1">Trades</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r, i) => (
                  <tr key={i} className="border-t border-[#F59E0B]/10" data-testid={`slow-run-${i}`}>
                    <td className="py-1.5 mono text-[10px] truncate max-w-[180px]">
                      {r.wall_timeout && <span className="text-[#F87171] mr-1">▲</span>}
                      {r.bot_name}
                    </td>
                    <td className="py-1.5 mono text-[10px] text-[#94A3B8]">{r.symbol}·{r.period}</td>
                    <td className="py-1.5 text-right mono text-[11px]" style={{ color: r.wall_timeout ? "#F87171" : "#F59E0B" }}>
                      {fmtSecs(r.backtest_seconds)}
                    </td>
                    <td className="py-1.5 text-right mono text-[10px] text-[#94A3B8]">{r.total_trades ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Slowest cells (symbol × period) */}
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-[#64748B] mb-2">Slowest cells (symbol · TF avg)</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="mono text-[9px] uppercase tracking-widest text-[#64748B]">
                  <th className="text-left py-1">Cell</th>
                  <th className="text-right py-1">Avg</th>
                  <th className="text-right py-1">N</th>
                  <th className="text-right py-1">Killed</th>
                </tr>
              </thead>
              <tbody>
                {cells.map((c, i) => (
                  <tr key={i} className="border-t border-[#F59E0B]/10" data-testid={`slow-cell-${i}`}>
                    <td className="py-1.5 mono text-[10px]">{c.cell}</td>
                    <td className="py-1.5 text-right mono text-[11px]" style={{ color: c.avg_seconds > 600 ? "#F87171" : c.avg_seconds > 300 ? "#F59E0B" : "#94A3B8" }}>
                      {fmtSecs(c.avg_seconds)}
                    </td>
                    <td className="py-1.5 text-right mono text-[10px] text-[#94A3B8]">{c.count}</td>
                    <td className="py-1.5 text-right mono text-[10px]" style={{ color: c.timeouts > 0 ? "#F87171" : "#64748B" }}>{c.timeouts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
